package com.dynamicruntime.common.schema

import com.dynamicruntime.common.annotation.KdrPrivate
import com.dynamicruntime.common.config.KdrConfigData
import com.dynamicruntime.common.context.KdrCxtBase
import com.dynamicruntime.common.exception.KdrException
import com.dynamicruntime.common.util.deepClone
import com.dynamicruntime.common.util.toOptT
import com.dynamicruntime.common.util.toT
import kotlin.reflect.KProperty

/**
 * Map-backed nullable delegate for a single schema attribute stored under [key].
 * Reading returns `null` when the key is absent; writing `null` removes it. Lets
 * a builder expose optional keywords as plain `var`s (`b.title = "..."`).
 */
class SchAttr<T>(private val data: MutableMap<String, Any?>, private val key: String) {
    operator fun getValue(thisRef: Any?, property: KProperty<*>): T? = data[key].toOptT()

    operator fun setValue(thisRef: Any?, property: KProperty<*>, value: T?) {
        if (value == null) data.remove(key) else data[key] = value
    }
}

/**
 * The `g-errors` block of a field: one function per [SchFailCode], plus [default] for anything not named.
 *
 * Named functions rather than a `code to message` map on purpose. A map would accept a misspelled key and
 * fail at boot; here the mistake cannot be written at all, and adding a failure code to the enum makes every
 * schema that should say something about it fail to compile until it does — which is the point at which
 * somebody is actually thinking about that code.
 */
class SchErrors(private val data: MutableMap<String, Any?>) {
    /** Shown when the field is required and was not supplied. */
    fun missingRequired(message: String) = set(SchFailCode.missingRequired, message)

    /** Shown when the value was the wrong shape before its content was even looked at. */
    fun wrongType(message: String) = set(SchFailCode.wrongType, message)

    /** Shown when the content was inspected and would not parse or coerce — a malformed date or number. */
    fun badValue(message: String) = set(SchFailCode.badValue, message)

    /** Shown when the value is not one of the field's declared options. */
    fun invalidOption(message: String) = set(SchFailCode.invalidOption, message)

    /**
     * Shown when the field was supplied but a conditional rule says it may not be, given some other field. Unused so
     * far, and that is fine: there is one function per failure code, whether or not a schema needs it yet.
     */
    fun notAllowed(message: String) = set(SchFailCode.notAllowed, message)

    /**
     * Shown when the value falls short of the field's lower bound — whichever of `minimum`, `minLength`,
     * `minItems` or `minProperties` its type declares. One function, because a field has one type and so one
     * of those can apply.
     */
    fun belowMinimum(message: String) = set(SchFailCode.belowMinimum, message)

    /** Shown when the value exceeds the field's upper bound; the counterpart of [belowMinimum]. */
    fun aboveMaximum(message: String) = set(SchFailCode.aboveMaximum, message)

    /** Shown when a string does not match the field's `pattern` -- the place to say what the pattern is for. */
    fun patternMismatch(message: String) = set(SchFailCode.patternMismatch, message)

    /**
     * Shown when an array declared `uniqueItems` holds the same item twice, or two items of a keyed type share a key
     * (issue #1521). Unused so far, and that is fine: there is one function per failure code, whether or not a schema
     * needs it yet.
     */
    fun duplicateItem(message: String) = set(SchFailCode.duplicateItem, message)

    /**
     * Shown for any failure this block does not name. Not itself a failure code — the fallback *after* the
     * specific ones and *before* the validator's built-in wording.
     */
    fun default(message: String) {
        data[SCH.errorDefault] = message
    }

    private fun set(code: SchFailCode, message: String) {
        data[code.name] = message
    }
}

/** Qualifies a bare type [name] with [namespace]. A name that already contains a
 *  '.' is treated as fully qualified and returned unchanged. */
fun qualifyTypeName(name: String, namespace: String): String =
    if (name.contains('.')) name else "$namespace.$name"

/** JSON Pointer to a type under `$defs`, e.g. "#/${'$'}defs/core.Count". A bare
 *  [name] is resolved within [namespace]; a dotted name is used as-is. */
fun typeRefPath(name: String, namespace: String): String =
    "#/${SCH.dDefs}/${qualifyTypeName(name, namespace)}"

/**
 * Builder for a single JSON Schema "type" (a schema node, usually an object
 * schema). Map-backed like the application-config builders ([KdrConfigData]), so
 * a built type is just an insertion-ordered `Map<String,Any?>` ready to be
 * rendered as JSON Schema.
 *
 * Carries the [namespace] it is declared in so that `$ref`s to sibling types can
 * be written with bare names. Required is tracked ON THE SIDE: marking a property
 * required records its name in this type's `required` array rather than as a flag
 * on the field.
 */
open class SchTypeBuilder(
    cxt: KdrCxtBase,
    val namespace: String,
    data: MutableMap<String, Any?> = LinkedHashMap(),
) : KdrConfigData(cxt, data) {

    var type: String? by SchAttr(data, SCH.type)
    var description: String? by SchAttr(data, SCH.description)
    /**
     * A short human name for the type -- what a page heads a trait's data with, and what a message calls it (a trait
     * lock's refusal, issue #857) -- beside [description]'s longer explanation. Absent, the pages humanize the id.
     */
    var title: String? by SchAttr(data, SCH.title)
    var format: String? by SchAttr(data, SCH.format)

    /** Custom `allowCoerce` keyword. When unset, the parser defaults it (true for
     *  numeric and boolean types and for a date `format`, false otherwise). */
    var allowCoerce: Boolean? by SchAttr(data, SCH.allowCoerce)

    /**
     * Custom `emptyIsAbsent` keyword: whether an empty value for this field means the field was not supplied.
     * When unset, the parser defaults it (true for scalars, false for arrays, objects, and untyped fields).
     *
     * Set it to **false** on a string field whose empty value is meaningful -- notably an update endpoint
     * where `""` means "clear this", as opposed to omitting the field to mean "leave it alone". Set it to
     * **true** on a list or object field to have an empty one read as no value at all.
     */
    var emptyIsAbsent: Boolean? by SchAttr(data, SCH.emptyIsAbsent)

    /**
     * Custom `g-visibleOnly` keyword (issue #543): every character of this string must render visibly. Admits
     * the ordinary space and refuses controls (tab, newline), format characters (zero-width and bidi marks),
     * and every other whitespace (no-break space, line separator). Off unless set; only a plain string field
     * may set it -- the parser refuses it on any other type, where it would constrain nothing.
     *
     * Reach for it on a name, a code, an identifier: anything a person types and another person reads, where
     * an invisible character is never intended and a look-alike one is the point of an attack.
     */
    var visibleOnly: Boolean? by SchAttr(data, SCH.visibleOnly)

    /**
     * Custom `g-outerWhitespace` keyword (issues #541, #765): [SOWS.trim] strips leading/trailing whitespace,
     * [SOWS.reject] fails a value that carries any, [SOWS.keep] leaves it alone. Unset is **not** [SOWS.keep]:
     * a plain string field with no declared mode is trimmed on the endpoint-input path by default (#765), so
     * reach for [preserveWhitespace] to opt an input field out. Only a plain string field may set it -- the
     * parser refuses it on any other type. Prefer the [trimmed] / [noOuterWhitespace] / [preserveWhitespace]
     * helpers to setting the raw value.
     */
    var outerWhitespace: String? by SchAttr(data, SCH.outerWhitespace)

    /**
     * Custom `g-visibleWhen` keyword (issue #545): a cfact expression deciding whether this property is shown to
     * a given caller. Evaluated on the frontend (issue #564) against the caller's delivered cfacts -- the served
     * schema keeps it, and the field is hidden from a caller whose cfacts do not satisfy the expression. Trait
     * data enforces it on every write (issue #830); a handler accepting it as an endpoint input must enforce it
     * itself, since request validation runs against the compiled schema that still carries it. See
     * [SCH.visibleWhen].
     */
    var visibleWhen: String? by SchAttr(data, SCH.visibleWhen)

    /**
     * Custom `g-sensitive` keyword (issue #1168): this field's value is a secret -- a password, a code, a token -- so
     * the request log shows that it was sent and redacts what it was. See [SCH.sensitive].
     */
    var sensitive: Boolean? by SchAttr(data, SCH.sensitive)

    /**
     * Whether undeclared properties are allowed. When unset, the parser defaults it (false when the type
     * has declared properties, true when it has none). Set explicitly to allow extras on a defined type.
     *
     * The keyword has a second use -- a schema, which makes the object a map ([mapOfValues]) -- and one type says
     * one or the other: setting this on a type already declared a map is refused, as [mapOfValues] refuses the
     * reverse, where the later of the two would otherwise quietly win.
     */
    var additionalProperties: Boolean?
        get() = data[SCH.additionalProperties] as? Boolean
        set(value) {
            if (data[SCH.additionalProperties] is Map<*, *>) throw mapAndSwitch()
            if (value == null) data.remove(SCH.additionalProperties) else data[SCH.additionalProperties] = value
        }

    private fun mapAndSwitch(): KdrException = KdrException.mkConv(
        "A type declares its undeclared properties once: as a map's values (mapOfValues) or as allowed or not " +
            "('${SCH.additionalProperties}' true or false), not both. A map already admits every key.",
    )

    // JSON Schema's four min/max pairs (issue #203). Each is spelled with the standard keyword for its type,
    // because that is what the document has to say; the parser folds whichever one applies into a single
    // bound on SchType, since a type declares at most one pair. A pair belonging to another type is ignored
    // rather than rejected -- that is what a standard validator does with an inapplicable keyword.

    /** Smallest accepted value, for a number or integer field. */
    var minimum: Number? by SchAttr(data, SCH.minimum)

    /** Largest accepted value, for a number or integer field. */
    var maximum: Number? by SchAttr(data, SCH.maximum)

    /** A value the number must exceed, for a number or integer field -- the bound itself is refused (issue #823). */
    var exclusiveMinimum: Number? by SchAttr(data, SCH.exclusiveMinimum)

    /**
     * A value the number must stay below; the counterpart of [exclusiveMinimum]. Unused so far, and that is fine: the
     * builder names every keyword the parser reads, whether or not a schema needs it yet.
     */
    var exclusiveMaximum: Number? by SchAttr(data, SCH.exclusiveMaximum)

    /** Fewest accepted characters, for a string field. Counted in code points, so an emoji counts once. */
    var minLength: Number? by SchAttr(data, SCH.minLength)

    /** Most accepted characters, for a string field; see [minLength] on how they are counted. */
    var maxLength: Number? by SchAttr(data, SCH.maxLength)

    /**
     * A regular expression a plain string field's value must contain a match of (issue #823) -- anchor it with
     * `^…$` to constrain the whole value. ECMA-262 syntax, restricted to what the backend and the browser read
     * alike; see [SchPattern] for what is refused and why. Say what it is for in [errors]' `patternMismatch`.
     */
    var pattern: String? by SchAttr(data, SCH.pattern)

    /** Fewest accepted elements, for an array field. */
    var minItems: Number? by SchAttr(data, SCH.minItems)

    /** Most accepted elements, for an array field. */
    var maxItems: Number? by SchAttr(data, SCH.maxItems)

    /** Whether an array field's elements must all differ, compared as JSON values (issue #823). */
    var uniqueItems: Boolean? by SchAttr(data, SCH.uniqueItems)

    /** Fewest accepted properties, for an object field. */
    var minProperties: Number? by SchAttr(data, SCH.minProperties)

    /** Most accepted properties, for an object field. */
    var maxProperties: Number? by SchAttr(data, SCH.maxProperties)

    /** JSON Schema `default` value. A required property with a default does not
     *  fail validation when missing, and coercion injects the default. */
    var default: Any? by SchAttr(data, SCH.default)

    /**
     * Custom `g-derived` keyword: this value is produced by something other than the caller (issue #254), so
     * it is neither asked for on the way in nor accepted from them.
     *
     * `true` is the everyday form and says only *that* it is produced elsewhere -- a code-backed pre-processor
     * supplies it. The parser also accepts an object, reserved for saying *how* once there is a language to
     * express a computation in; accepting both now means widening later is not a migration of stored
     * documents.
     */
    var derived: Any? by SchAttr(data, SCH.derived)

    /**
     * Declares the functions run on this type's data each time it is written (issue #1501), as `g-onWrite`: each a
     * usage object naming its kind in `fn` -- `computeValue(...)` in `base:common` builds one. Replaces any declared
     * before; on a trait's data type, they run after an edit is merged.
     */
    fun onWrite(vararg usages: Map<String, Any?>) {
        data[SCH.onWrite] = usages.toList()
    }

    /**
     * Declares the fields, in order, that tell one value of this type from another of it (issues #487, #1521), as
     * `g-primaryKey`: on a trait's data type, they key its entries -- one entry per key value -- and on the items of
     * an array, they tell its elements apart, so two elements with one key are refused. Each field must be declared
     * here, required, a scalar, and not derived; the parse refuses a key that is not. Replaces any declared before.
     */
    fun primaryKey(vararg fields: String) {
        data[SCH.primaryKey] = fields.toList()
    }

    /**
     * A read-only **presentation hint** (issue #540): a [PRES] value saying how a display surface should show
     * this type/field (`status`/`table`/`identifier`). Advisory only -- it never touches validation. Set as
     * `presentation = PRES.table`, mirroring how the wire keyword ([SCH.presentation]) reads.
     */
    var presentation: String? by SchAttr(data, SCH.presentation)

    /**
     * JSON Schema `const`: the one value this field admits.
     *
     * Its everyday use is a union branch declaring which branch it is — and there it is what makes the branch
     * selectable **without** our discriminator keyword, so a stock validator trying every branch reaches the
     * same answer we do. Set through "variantBranch" rather than by hand for that case.
     */
    var const: Any? by SchAttr(data, SCH.const)

    /**
     * Declares that [field] is present exactly when [on] holds [value] — JSON Schema's `if`/`then`/`else`,
     * emitted in the one shape this layer reads (issue #253).
     *
     * The commonest shape a trait's data takes: `{"hasValue": true, "value": "approve"}`, where `value` is
     * required when the box is ticked and inadmissible when it is not, written as
     * `presentWhen("value", on = "hasValue", value = true)`.
     *
     * **The emitted `if` repeats `required`, and that is the point of having a helper at all.** JSON Schema's
     * `if` evaluates a subschema, and a bare `{"properties": {"hasValue": {"const": true}}}` passes
     * *vacuously* when the property is absent — so an omitted `hasValue` would satisfy it, the `then` clause
     * would fire, and the validator would demand `value` from a payload that said nothing. Written by hand
     * that trap is invisible until someone submits an empty form; written here it is impossible.
     *
     * At most one per type, since JSON Schema allows one `if` per schema object — see [SchCondition].
     */
    fun presentWhen(field: String, on: String, value: Any?) {
        data[SCH.kIf] = linkedMapOf<String, Any?>(
            SCH.required to listOf(on),
            SCH.properties to linkedMapOf(on to linkedMapOf(SCH.const to value)),
        )
        data[SCH.kThen] = linkedMapOf<String, Any?>(SCH.required to listOf(field))
        data[SCH.kElse] = linkedMapOf<String, Any?>(
            SCH.not to linkedMapOf<String, Any?>(SCH.required to listOf(field)),
        )
    }

    /**
     * Declares this named type as an **extension** of [base] (issue #990): the base's body plus what this block adds --
     * properties merged by default (a named one added or replacing the base's), the layout merged by field, every
     * other key replacing. Resolved before the document is parsed, so it is served and validated as one type. A bare
     * [base] resolves within this builder's [namespace]; a dotted name is used as-is. Only for a top-level type.
     */
    fun extends(base: String) {
        data[SCH.extends] = qualifyTypeName(base, namespace)
    }

    /**
     * Makes this schema a `$ref` to another type. A bare [name] resolves within
     * this builder's [namespace]; a dotted name (e.g. "core.Count") is used as-is.
     */
    fun ref(name: String) {
        data[SCH.dRef] = typeRefPath(name, namespace)
    }

    /**
     * Declares a property **as it already is** -- an empty body, which an overlay reads as "inherit".
     *
     * Only meaningful when a client alters a type. Mentioning keys is how the property set is reduced, so an
     * alteration that changes one property still has to name every property it keeps; without this each of
     * them would have to be restated in full, which is the restating this whole design exists to avoid --
     * and a restatement drifts from what it copied the moment the original changes.
     *
     * There is deliberately no description: this says nothing about the property, which is the point.
     */
    fun keepProperty(name: String) {
        propertiesMap()[name] = LinkedHashMap<String, Any?>()
    }

    /**
     * Adds a choice to the custom `options` construct: a [value] (the stored data)
     * and an optional display [label], which defaults to the value when redundant.
     */
    fun option(value: String, label: String = value) {
        optionsList().add(linkedMapOf(SCH.label to label, SCH.value to value))
    }

    /**
     * Declares this attribute's choices to be **suggestions rather than a bound** (issue #418): the value may
     * be one of them or anything else the field's other constraints admit.
     *
     * Reach for it whenever the list cannot claim to be complete -- one drawn from a table, or from what this
     * caller has permission to name. A closed list is a promise that nothing else is valid, and a list that
     * varies is in no position to make it. See [SCH.openOptions].
     */
    fun openOptions() {
        data[SCH.openOptions] = true
    }

    /**
     * Sources this attribute's choices from the callback registered under [id], instead of writing them into
     * the document (issue #413). See [SCH.optionsSource] for what that means for validation and for what a
     * reader ends up seeing.
     *
     * Declaring both this and [option] choices fails the boot rather than here, because the two calls may
     * arrive in either order: a check in this method would catch `option(...)` then `optionsSource(...)` and
     * miss the reverse, which is the sort of half-check that reads as coverage and is not.
     */
    fun optionsSource(id: String) {
        data[SCH.optionsSource] = id
    }

    /**
     * Adds every value of an enum as a choice ([Enum.name] is the stored value), so the enum is the single
     * source of truth for a field's valid values -- the schema constraint here, the edge validation the
     * validator runs from it, and the code's `when` all follow the same "enum" e.g., `options(ClockOp.entries)`.
     */
    fun options(values: Iterable<Enum<*>>) {
        for (v in values) option(v.name)
    }

    @KdrPrivate
    fun optionsList(): MutableList<Any?> =
        data.getOrPut(SCH.options) { ArrayList<Any?>() }.toT()

    /**
     * Declares what a failure against this field should say, in place of the validator's own wording
     * (issue #202):
     *
     * ```
     * property("score", "Numeric score.", required = true) {
     *     type = SCT.integer
     *     errors {
     *         missingRequired("Please enter a score.")
     *         default("That score does not look right.")
     *     }
     * }
     * ```
     *
     * The block offers one function per failure code, so a code that does not exist cannot be written — which
     * is why the parser's check on the same keys only has to catch documents that were not built here.
     */
    fun errors(build: SchErrors.() -> Unit) {
        SchErrors(data.getOrPut(SCH.errors) { LinkedHashMap<String, Any?>() }.toT()).apply(build)
    }

    /**
     * Whether this was deliberately left **unconstrained**: any JSON value validates.
     *
     * Set through [anyValue], and distinct from simply not having said anything -- an untyped property
     * otherwise defaults to `string`, which is the right guess for a field somebody forgot to type and the
     * wrong one for a field that genuinely holds more than one shape.
     */
    var isUnconstrained: Boolean = false
        private set

    /**
     * Declares this property as holding **any** JSON value, suppressing the string default.
     *
     * For a value whose shape the `Sch*` layer cannot yet state. The honest use is narrow: a field that is
     * genuinely two shapes, where declaring either one would be a schema that rejects half the values the
     * endpoint really sends. `SchType.jsonType` has always been nullable for exactly this, and the wire form
     * is a schema with no `type`, which is what JSON Schema means by "any value".
     *
     * Prefer a real type wherever there is one. This says "we cannot express this yet", not "anything goes".
     */
    fun anyValue() {
        isUnconstrained = true
        data.remove(SCH.type)
    }

    /**
     * Adds a property (field) subschema. A [description] is MANDATORY for fields
     * (unlike a type's description, which is optional). The field's type defaults
     * to `string` unless [build] sets a `type` or makes it a `$ref`. When
     * [required] is true the property name is also added to this type's `required`
     * array (required is tracked on the side).
     */
    fun property(
        name: String,
        description: String,
        required: Boolean = false,
        build: SchTypeBuilder.() -> Unit = {},
    ) {
        val sub = SchTypeBuilder(cxt, namespace)
        sub.description = description
        sub.apply(build)
        // Default field type to string unless the build set a type or a $ref -- or said outright that the
        // property is unconstrained, which is a different thing from having forgotten to say.
        if (!sub.isUnconstrained && SCH.type !in sub.data && SCH.dRef !in sub.data) {
            sub.type = SCT.string
        }
        propertiesMap()[name] = sub.data
        if (required) this.required(name)
    }

    /**
     * Adds a previously declared, reusable [property] (see [schemaProperty]). The
     * property's schema is deep-cloned, so the original is untouched, then [mutate]
     * can adjust the clone for this use (e.g., refine its description or
     * constraints). When [required] is true its name is added to `required`.
     */
    fun property(property: SchBuilderProperty, required: Boolean = false, mutate: SchTypeBuilder.() -> Unit = {}) {
        val sub = SchTypeBuilder(cxt, namespace, property.data.deepClone())
        sub.apply(mutate)
        propertiesMap()[property.name] = sub.data
        if (required) this.required(property.name)
    }

    private fun propertiesMap(): MutableMap<String, Any?> =
        data.getOrPut(SCH.properties) { LinkedHashMap<String, Any?>() }.toT()

    /** Records one or more property names in this type's `required` array. */
    fun required(vararg names: String) {
        val req: MutableList<String> = data.getOrPut(SCH.required) { ArrayList<String>() }.toT()
        for (n in names) if (n !in req) req.add(n)
    }

    /** Defines the element schema for an array type (`items`). */
    fun items(build: SchTypeBuilder.() -> Unit) {
        data[SCH.items] = SchTypeBuilder(cxt, namespace).apply(build).data
    }

    /**
     * Makes this an object that is a **map** (issue #1055): free keys, each holding a value of the schema [build]
     * declares -- JSON Schema's `additionalProperties` given as a schema. What `items` is to an array's elements,
     * for an object's undeclared properties: `mapOfValues { type = SCT.string }` is a map of names to text, and
     * one nested in another a map of maps. Sets the type to an object; properties declared beside it are
     * validated as themselves.
     *
     * Named for what it makes rather than for the keyword, since the keyword's other use -- true or false, whether
     * a record admits keys it does not declare -- is [additionalProperties], and one name for both would read as a
     * switch where this is a declaration.
     */
    fun mapOfValues(build: SchTypeBuilder.() -> Unit) {
        if (data[SCH.additionalProperties] is Boolean) throw mapAndSwitch()
        type = SCT.kObject
        data[SCH.additionalProperties] = SchTypeBuilder(cxt, namespace).apply(build).data
    }

    /**
     * Strips leading/trailing whitespace from this string value on **every** path (issues #541, #765):
     * `g-outerWhitespace: "trim"`. Endpoint input is already trimmed by default, so reach for this only to force
     * the strip on the output/stored path too; for ordinary input free text the default already covers it.
     */
    fun trimmed() {
        outerWhitespace = SOWS.trim
    }

    /**
     * Refuses a value carrying leading/trailing whitespace on this string field (issue #541):
     * `g-outerWhitespace: "reject"`. For a code or identifier, where silent trimming would hide a paste error
     * rather than fix it.
     */
    fun noOuterWhitespace() {
        outerWhitespace = SOWS.reject
    }

    /**
     * Opts this string field out of the input-path trim default (issue #765): `g-outerWhitespace: "keep"`, so
     * leading/trailing whitespace survives even as endpoint input. For the rare field whose edge whitespace is
     * content -- a password, a free-text block, a value meant to round-trip verbatim -- rather than a paste
     * artifact.
     */
    fun preserveWhitespace() {
        outerWhitespace = SOWS.keep
    }

    /** Marks this schema as a day-only date string (JSON Schema `format: "date"`, e.g. `2021-06-01`). */
    fun dayOnlyDate() {
        type = SCT.string
        format = SFMT.date
    }

    /** Marks this schema as a full date-time string (JSON Schema `format: "date-time"`). */
    fun dateTime() {
        type = SCT.string
        format = SFMT.dateTime
    }

    /**
     * Marks this schema as **file content**: OpenAPI's `{"type": "string", "format": "binary"}`.
     *
     * On an endpoint's input field it declares an upload — the request arrives as `multipart/form-data` and
     * the field's value is a `ContentData`. On an endpoint's output it declares that the response body *is*
     * the file rather than a JSON envelope. See [SFMT.binary] for why a file is spelled as a string.
     */
    fun binaryContent() {
        type = SCT.string
        format = SFMT.binary
    }

    /**
     * Declares this property's value to be a **JSON Schema type body** (issue #316), validated by *parsing* it
     * -- see `validateSchemaDocument` -- after its shape is checked against the generated schema for schema
     * (issue #1056), which reports every keyword at fault by path. An open object on the wire; the parser is the
     * authority, so whatever it refuses is reported and whatever it does not understand is not claimed to have
     * been checked.
     */
    fun schemaDocument() {
        type = SCT.kObject
        data[SCH.schemaDocument] = true
    }

    /**
     * Declares that this object takes a `$comment` (issue #1135): a note for whoever maintains the definition,
     * which changes nothing about what it does, and is stored and read back with it. Declared on purpose, and only
     * on an object an author would want to explain that has no author-facing text of its own -- see
     * [reservedSchemaKeys] for the rule, and why a `$comment` anywhere else is refused.
     */
    fun commentProperty() {
        property(SCH.dComment, "A note for whoever maintains this definition; it changes nothing about what the definition does.")
    }
}
